import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  getAnalyticsMeasurementId,
  type AnalyticsConsentState,
} from "../consent";

type CookieConsentContextValue = {
  consent: AnalyticsConsentState;
  hasAnalyticsConfigured: boolean;
  isReady: boolean;
  isPreferencesOpen: boolean;
  acceptAnalytics: () => void;
  declineAnalytics: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function readStoredConsent(): AnalyticsConsentState {
  if (typeof window === "undefined") {
    return "unset";
  }

  const storedValue = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
  return storedValue === "granted" || storedValue === "denied" ? storedValue : "unset";
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<AnalyticsConsentState>("unset");
  const [isReady, setIsReady] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const hasAnalyticsConfigured = getAnalyticsMeasurementId().length > 0;

  useEffect(() => {
    if (!hasAnalyticsConfigured) {
      setIsReady(true);
      return;
    }

    setConsent(readStoredConsent());
    setIsReady(true);
  }, [hasAnalyticsConfigured]);

  function updateConsent(nextConsent: Exclude<AnalyticsConsentState, "unset">) {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, nextConsent);
    }

    setConsent(nextConsent);
    setIsPreferencesOpen(false);
  }

  function acceptAnalytics() {
    updateConsent("granted");
  }

  function declineAnalytics() {
    updateConsent("denied");
  }

  function openPreferences() {
    setIsPreferencesOpen(true);
  }

  function closePreferences() {
    setIsPreferencesOpen(false);
  }

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasAnalyticsConfigured,
        isReady,
        isPreferencesOpen,
        acceptAnalytics,
        declineAnalytics,
        openPreferences,
        closePreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);

  if (!context) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }

  return context;
}
